import { useMemo } from 'react';
import { DataItem, DataTableColumn, FilterItem } from '../utils/types';

interface UseDataProcessingParams {
  data: DataItem[];
  columns: DataTableColumn[];
  appliedFilters: FilterItem[];
  sortedColumn: string;
  sortDirection: 'asc' | 'desc' | '';
  rowsPerPage: number;
  currentPage: number;
}

interface ProcessedData {
  pagedData: DataItem[];
}

const compareValues = (a: any, b: any): number => {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  return a < b ? -1 : 1;
};

const filterData = (data: DataItem[], filters: FilterItem[]): DataItem[] => {
  if (!filters.length) return data;

  return data.filter((item) =>
    filters.every((f) => {
      if (f.key === 'createdAt' && f.value.includes(',')) {
        const [from, to] = f.value.split(',');
        if (!from || !to) return true;
        const itemDate = new Date(item.createdAt as string);
        return itemDate >= new Date(from) && itemDate <= new Date(to);
      }
      const itemValue = item[f.key]?.toString().toLowerCase() ?? '';
      const filterValue = f.value.toLowerCase();
      return itemValue.startsWith(filterValue);
    }),
  );
};

const sortData = (
  data: DataItem[],
  column: string,
  direction: 'asc' | 'desc' | '',
  columns: DataTableColumn[],
): DataItem[] => {
  if (!column || !direction) return data;

  const col = columns.find((c) => c.key === column);
  if (!col) return data;

  const sortField = col.sortKey || col.key;
  const multiplier = direction === 'asc' ? 1 : -1;

  return [...data].sort((a, b) => {
    const valA = (a[sortField]?.toString() ?? '').toLowerCase();
    const valB = (b[sortField]?.toString() ?? '').toLowerCase();
    return compareValues(valA, valB) * multiplier;
  });
};

const paginateData = (
  data: DataItem[],
  page: number,
  pageSize: number,
): DataItem[] => {
  const startIndex = (page - 1) * pageSize;
  return data.slice(startIndex, startIndex + pageSize);
};

export function useDataProcessing({
  data,
  columns,
  sortedColumn,
  sortDirection,
  rowsPerPage,
  currentPage,
}: UseDataProcessingParams): ProcessedData {

  const sortedData = useMemo(
    () => sortData(data, sortedColumn, sortDirection, columns),
    [data, sortedColumn, sortDirection, columns],
  );

  const pagedData = useMemo(
    () => paginateData(sortedData, currentPage, rowsPerPage),
    [sortedData, currentPage, rowsPerPage],
  );

  return { pagedData };
}
